import React, { useState, useEffect, useCallback } from 'react';
import { clustersAPI, gpuAPI, appServicesAPI } from '../services/api';
import { useNamespaces } from '../pages/LivePage';

// Recover the K8s `app=` label a CPU/app instance's pods carry from its (possibly
// drifted) app-service name — mirrors the backend _instance_app_label(): strip a
// trailing version tag then one known sub-object suffix.
function cpuAppLabel(name) {
  let n = (name || '').trim().replace(/-v\d+$/, '');
  for (const suf of ['-init', '-svc', '-secret', '-config', '-cm']) {
    if (n.endsWith(suf)) { n = n.slice(0, -suf.length); break; }
  }
  return n;
}

const lbl = (text) => <label style={{ fontSize:12, fontWeight:600, color:'var(--ink-2)',
  display:'block', marginBottom:4 }}>{text}</label>;
const hint = (children, color = 'var(--muted-2)') =>
  <div style={{ fontSize:11, color, marginTop:4 }}>{children}</div>;

// Label keys/values are restricted to this charset by Kubernetes, which also keeps them
// safe inside the single-quoted shell `echo` the create command uses.
const LABEL_RE = /^[A-Za-z0-9]([A-Za-z0-9._/-]*[A-Za-z0-9])?$/;

/** Deployments + StatefulSets in one namespace, with their real selector and ports. */
function useNamespaceWorkloads(clusterId, namespace) {
  const [workloads, setWorkloads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    if (!clusterId || !namespace) { setWorkloads([]); return; }
    setLoading(true); setFailed(false);
    const opts = { pageSize: 500, namespace };
    const [dep, sts] = await Promise.allSettled([
      clustersAPI.getWorkloadPaged('deployments', clusterId, opts),
      clustersAPI.getWorkloadPaged('statefulsets', clusterId, opts),
    ]);
    const rows = (res, kind) => (res.status === 'fulfilled' ? (res.value.data?.items || []) : [])
      .map(w => ({ ...w, kind, key: `${kind}/${w.name}` }));
    setWorkloads([...rows(dep, 'Deployment'), ...rows(sts, 'StatefulSet')]
      .sort((a, b) => a.name.localeCompare(b.name)));
    setFailed(dep.status === 'rejected' && sts.status === 'rejected');
    setLoading(false);
  }, [clusterId, namespace]);

  useEffect(() => { load(); }, [load]);
  return { workloads, loading, failed, reload: load };
}

/** Provisioned GPU instances and CPU/app instances a Service can bind to. */
function useInstances(clusterId) {
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setFailed(false);
    const [gpuRes, cpuRes] = await Promise.allSettled([
      gpuAPI.listRequests(), appServicesAPI.list(clusterId, false), // live=false: fast, no agent round-trip
    ]);
    const gpu = (gpuRes.status === 'fulfilled' ? (gpuRes.value.data || []) : [])
      .filter(x => x.status === 'provisioned' || x.status === 'approved')
      .map(x => ({
        key: `gpu:${x.id}`, label: `GPU #${x.id} · ${x.project_name || 'instance'}`,
        namespace: 'gpu-instances', selector: { 'sarvkube-req': String(x.id) },
        name_hint: `svc-req${x.id}`,
      }));
    // De-dup by the resolved app label so -svc/-secret/-vN artifacts don't clutter it.
    const seen = new Set();
    const cpu = (cpuRes.status === 'fulfilled' ? (cpuRes.value.data || []) : [])
      .filter(x => ['Deployment', 'StatefulSet', 'Pod'].includes(x.kind))
      .map(x => {
        const app = cpuAppLabel(x.name);
        return {
          key: `cpu:${app}:${x.namespace}`, label: `CPU · ${app} (${x.namespace})`,
          namespace: x.namespace || 'cpu-instances', selector: { app }, name_hint: `${app}-svc`,
        };
      })
      .filter(x => (seen.has(x.key) ? false : (seen.add(x.key), true)));
    const merged = [...cpu, ...gpu];
    setInstances(merged);
    // Only an error when a call rejected AND nothing came back (agent blip), as
    // distinct from a cluster that genuinely has no instances.
    setFailed((gpuRes.status === 'rejected' || cpuRes.status === 'rejected') && merged.length === 0);
    setLoading(false);
  }, [clusterId]);

  useEffect(() => { load(); }, [load]);
  return { instances, loading, failed, reload: load };
}

const toRows = (sel) => {
  const rows = Object.entries(sel || {}).map(([k, v]) => ({ k, v: String(v) }));
  return rows.length ? rows : [{ k: 'app', v: '' }];
};

function buildServiceYaml(form, selector, svcPort, targetPort) {
  const sel = selector.map(r => `    ${r.k}: ${JSON.stringify(r.v)}`).join('\n');
  const nodePort = form.type === 'NodePort' && form.nodePort ? `\n    nodePort: ${form.nodePort}` : '';
  return `apiVersion: v1
kind: Service
metadata:
  name: ${form.name}
  namespace: ${form.namespace}
spec:
  type: ${form.type}
  selector:
${sel}
  ports:
  - port: ${svcPort}
    targetPort: ${targetPort}
    protocol: TCP${nodePort}`;
}

function SelectorEditor({ rows, setRows }) {
  const setRow = (i, key, val) => setRows(rs => rs.map((r, j) => (j === i ? { ...r, [key]: val } : r)));
  return (
    <div>
      {rows.map((r, i) => (
        <div key={i} style={{ display:'grid', gridTemplateColumns:'1fr 1fr auto', gap:8, marginBottom:6 }}>
          <input placeholder="app" value={r.k} onChange={e => setRow(i, 'k', e.target.value.trim())} className="input" />
          <input placeholder="my-app" value={r.v} onChange={e => setRow(i, 'v', e.target.value.trim())} className="input" />
          <button type="button" className="btn btn-ghost btn-icon" title="Remove label"
            disabled={rows.length === 1} onClick={() => setRows(rs => rs.filter((_, j) => j !== i))}>✕</button>
        </div>
      ))}
      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setRows(rs => [...rs, { k: '', v: '' }])}>
        + Add label
      </button>
    </div>
  );
}

export default function CreateServiceModal({ clusterId, initialNamespace = '', onClose, onCreated }) {
  const [form, setForm] = useState({
    name:'', namespace: initialNamespace, type:'ClusterIP', port:'', targetPort:'', nodePort:'',
  });
  const [selector, setSelector] = useState([{ k: 'app', v: '' }]);
  const [workloadKey, setWorkloadKey] = useState('');
  const [instanceKey, setInstanceKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const { namespaces, ns: defaultNs } = useNamespaces(clusterId);
  useEffect(() => {
    if (!form.namespace && defaultNs) setForm(f => ({ ...f, namespace: defaultNs }));
  }, [defaultNs, form.namespace]);

  const wl = useNamespaceWorkloads(clusterId, form.namespace);
  const inst = useInstances(clusterId);
  const picked = wl.workloads.find(w => w.key === workloadKey);

  const changeNamespace = (ns) => {
    setF('namespace', ns);
    setWorkloadKey('');          // the old pick belongs to the previous namespace
  };

  // Picking a deployment copies its REAL pod selector (every matchLabels entry) — a
  // Service whose selector misses even one label routes to nothing.
  const pickWorkload = (key) => {
    setWorkloadKey(key);
    setInstanceKey('');
    const w = wl.workloads.find(x => x.key === key);
    if (!w) return;
    setSelector(toRows(w.selector));
    const firstPort = w.container_ports?.[0]?.port;
    setForm(f => ({
      ...f,
      name: f.name || `${w.name}-svc`.slice(0, 63),
      targetPort: f.targetPort || (firstPort ? String(firstPort) : ''),
      port: f.port || (firstPort ? String(firstPort) : ''),
    }));
  };

  const pickInstance = (key) => {
    setInstanceKey(key);
    const x = inst.instances.find(i => i.key === key);
    if (!x) return;
    setWorkloadKey('');
    setSelector(toRows(x.selector));
    setForm(f => ({
      ...f,
      namespace: x.namespace,
      type: f.type === 'ClusterIP' ? 'NodePort' : f.type,  // external by default for instances
      name: f.name || x.name_hint,
    }));
  };

  const validate = () => {
    if (!form.name) return 'Name is required';
    if (!form.namespace) return 'Namespace is required';
    if (selector.some(r => !r.k || !r.v)) return 'Every selector label needs a key and a value (or pick a deployment above)';
    const bad = selector.find(r => !LABEL_RE.test(r.k) || !LABEL_RE.test(r.v));
    if (bad) return `Invalid selector label ${bad.k}=${bad.v}`;
    return '';
  };

  const handleCreate = async () => {
    const v = validate();
    if (v) { setError(v); return; }
    // Port (service) is REQUIRED by Kubernetes; default each port to the other.
    const svcPort    = (form.port && String(form.port).trim()) || form.targetPort;
    const targetPort = (form.targetPort && String(form.targetPort).trim()) || svcPort;
    if (!svcPort) { setError('Port is required'); return; }

    setSaving(true); setError('');
    try {
      const yaml = buildServiceYaml(form, selector, svcPort, targetPort);
      const cmd = `kubectl create namespace ${form.namespace} --dry-run=client -o yaml | kubectl apply -f - 2>/dev/null; echo '${yaml}' | kubectl apply -f -`;
      const r = await clustersAPI.exec(clusterId, cmd, { section: 'networking', action: 'create' });
      // Surface BOTH stdout and stderr — kubectl errors come back on stderr, and
      // swallowing them made failures look like "nothing happened".
      const combined = `${r.data?.output || r.data?.stdout || ''}\n${r.data?.stderr || ''}`.trim();
      const ok = /(^|\s)service\/\S+ (created|configured|unchanged)/.test(combined)
                 || (r.data?.exit_code === 0 && /created|configured|unchanged/.test(combined));
      if (ok) { onCreated(); onClose(); }
      else setError(combined || 'Create failed (no output from cluster — is the cluster agent connected?)');
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Create failed');
    } finally { setSaving(false); }
  };

  const ports = picked?.container_ports || [];

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000,
      display:'flex', alignItems:'center', justifyContent:'center' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="card" style={{ width:520, maxHeight:'90vh', overflowY:'auto' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
          <h2 style={{ margin:0, fontSize:16, fontWeight:700 }}>Create Service</h2>
          <button onClick={onClose} className="btn btn-ghost btn-icon" style={{ fontSize:20 }}>✕</button>
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div>
              {lbl('Namespace *')}
              <select value={form.namespace} onChange={e => changeNamespace(e.target.value)} className="select">
                {namespaces.length === 0 && <option value="">Loading namespaces…</option>}
                {namespaces.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div>
              {lbl('Deployment')}
              <select value={workloadKey} onChange={e => pickWorkload(e.target.value)} className="select"
                disabled={wl.loading || !form.namespace}>
                <option value="">
                  {wl.loading ? 'Loading…' : wl.workloads.length ? '— pick a workload —' : 'None in this namespace'}
                </option>
                {wl.workloads.map(w => (
                  <option key={w.key} value={w.key}>
                    {w.name}{w.kind === 'StatefulSet' ? ' (StatefulSet)' : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {wl.failed
            ? hint(<>⚠ Couldn’t load workloads. <button type="button" onClick={wl.reload}
                style={{ background:'none', border:'none', padding:0, color:'var(--brand)', cursor:'pointer',
                  font:'inherit', textDecoration:'underline' }}>Retry</button> or set the selector manually.</>,
                'var(--color-warning-600, #b45309)')
            : hint('Picking a deployment fills the selector with its pod labels, plus the port and name.')}

          <div>
            {lbl('…or bind to an Instance')}
            <select value={instanceKey} onChange={e => pickInstance(e.target.value)} className="select" disabled={inst.loading}>
              <option value="">{inst.loading ? 'Loading instances…' : '— none —'}</option>
              {inst.instances.map(x => <option key={x.key} value={x.key}>{x.label}</option>)}
            </select>
            {inst.failed && hint(<>⚠ Couldn’t load instances. <button type="button" onClick={inst.reload}
              style={{ background:'none', border:'none', padding:0, color:'var(--brand)', cursor:'pointer',
                font:'inherit', textDecoration:'underline' }}>Retry</button></>, 'var(--color-warning-600, #b45309)')}
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div>{lbl('Name *')}<input placeholder="my-service" value={form.name}
              onChange={e => setF('name', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))} className="input" /></div>
            <div>
              {lbl('Type')}
              <select value={form.type} onChange={e => setF('type', e.target.value)} className="select">
                <option value="ClusterIP">ClusterIP (internal)</option>
                <option value="NodePort">NodePort (external)</option>
                <option value="LoadBalancer">LoadBalancer</option>
              </select>
            </div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div>
              {lbl('Target Port (in pod)')}
              <input type="number" placeholder="e.g. 3000" value={form.targetPort} list="svc-container-ports"
                onChange={e => {
                  const v = e.target.value;
                  // Mirror into Port while Port is untouched, so it is never left blank.
                  setForm(f => ({ ...f, targetPort: v, port: (f.port === '' || f.port === f.targetPort) ? v : f.port }));
                }} className="input" />
              <datalist id="svc-container-ports">
                {ports.map(p => <option key={`${p.container}:${p.port}`} value={p.port}>{p.container}{p.name ? ` · ${p.name}` : ''}</option>)}
              </datalist>
              {picked && !ports.length && hint('This workload declares no container ports — enter the one it listens on.')}
            </div>
            <div>
              {lbl('Port (service)')}
              <input type="number" placeholder="defaults to Target Port" value={form.port}
                onChange={e => setF('port', e.target.value)} className="input" />
            </div>
          </div>
          {form.type === 'NodePort' && (
            <div>
              {lbl('Node Port (external, 30000–32767)')}
              <input type="number" min="30000" max="32767" placeholder="e.g. 30010"
                value={form.nodePort || ''} onChange={e => setF('nodePort', e.target.value)} className="input" />
              {hint('Public port on the server IP. Leave blank to let Kubernetes auto-assign.')}
            </div>
          )}
          <div>
            {lbl('Selector (pod labels to route to)')}
            <SelectorEditor rows={selector} setRows={setSelector} />
            {hint(`Routes traffic to pods with ${selector.map(r => `${r.k || '…'}=${r.v || '…'}`).join(', ')}`)}
          </div>
          {error && <div className="banner danger">{error}</div>}
          <div style={{ display:'flex', gap:8, marginTop:4 }}>
            <button onClick={handleCreate} disabled={saving} className="btn btn-primary" style={{ flex:1 }}>
              {saving ? '⟳ Creating...' : '+ Create Service'}
            </button>
            <button onClick={onClose} className="btn btn-secondary">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}
